import {
  type ChartDataset,
  type ScriptableTooltipContext,
  type TooltipModel,
} from "chart.js";
import { orderBy } from "lodash";

import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { REVENUE_ID, ROAS_ID, OBSERVED_SPEND_ID } from "./const";
import { getChartData } from "./plugins/chart-data-manager-plugin";

export function chartTooltip(context: ScriptableTooltipContext<"line">) {
  const chart = context.chart;
  const tooltip = context.tooltip;
  const options = context.tooltip.options;

  const datasetIndexById: Record<string, number> = {};
  chart.data.datasets.forEach((dataset, idx) => {
    datasetIndexById[dataset.yAxisID ?? ""] = idx;
  });

  const observedSpendIsHidden = datasetIndexById[OBSERVED_SPEND_ID]
    ? chart.config.data.datasets[datasetIndexById[OBSERVED_SPEND_ID]]?.hidden
    : true;

  const allConsistentDataIsHidden = !Object.keys(datasetIndexById)
    .filter((id) => id !== OBSERVED_SPEND_ID)
    .map(
      (id) =>
        Number.isInteger(datasetIndexById[id]) &&
        chart.config.data.datasets[datasetIndexById[id]]?.hidden,
    )
    .some((val) => !val);

  const observedSpendIsSelectedAlone =
    !observedSpendIsHidden && allConsistentDataIsHidden;

  const tooltipInteractingWithOneRelevantDataPoint =
    tooltip.dataPoints.length === 1;

  let tooltipEl = chart.canvas?.parentNode?.querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    chart.canvas.parentNode?.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (
    !observedSpendIsHidden &&
    !observedSpendIsSelectedAlone &&
    tooltipInteractingWithOneRelevantDataPoint
  ) {
    return;
  }

  if (tooltip.body) {
    const content = buildTooltipContent(
      tooltip,
      options,
      observedSpendIsSelectedAlone,
    );

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          class="
            bg-v2-primary border border-v2-secondary py-3 px-4 rounded-md space-y-2
            shadow whitespace-nowrap ${contentPosition} min-w-[25rem]
          "
        >
          ${content}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    left: positionX + tooltip.caretX + "px",
    top: positionY + tooltip.caretY + "px",
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

const buildTooltipContent = (
  tooltip: TooltipModel<"line" | "bar">,
  options: any,
  observedSpendIsSelectedAlone: boolean,
) => {
  const dataIndex =
    tooltip.dataPoints.find(
      (dataPoint) => dataPoint.dataset.yAxisID !== OBSERVED_SPEND_ID,
    )?.dataIndex ?? 0;

  const getSpendForTooltipLocation = () => {
    if (!observedSpendIsSelectedAlone) {
      const labels = tooltip.chart.data.labels as number[] | undefined;
      if (labels && dataIndex) {
        return labels[dataIndex];
      }
      return null;
    }
    return null;
  };

  const getSpendSection = () => {
    const spendValue = getSpendForTooltipLocation();
    if (spendValue) {
      return `
      <div class="flex space-x-0.5 items-center">
        <div>Spend</div>
      </div>
      <div class="text-right font-mono">${MetricFormatters.currency(
        spendValue,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )}</div>

  `;
    }

    return "";
  };

  // @ts-ignore
  const datasets = tooltip._chart.data.datasets;

  const startingRangeDataset = datasets.find(
    (d: any) => d.id === "0.16",
  ) as ChartDataset<"line" | "bar", unknown[]> & {
    borderDash: number[];
  };
  const endingRangeDataset = datasets.find(
    (d: any) => d.id === "0.84",
  ) as ChartDataset<"line" | "bar", unknown[]> & {
    borderDash: number[];
  };

  const quantileDatasets = datasets.filter((d: any) => !!d.id) as (ChartDataset<
    "line" | "bar",
    unknown[]
  > & {
    borderDash: number[];
    id: string;
  })[];

  const getTooltipDataPointsData = () => {
    return orderBy(
      datasets.filter(
        (
          d: ChartDataset<"line" | "bar", unknown[]> & {
            borderDash: number[];
            id: string;
          },
        ) => !d.id,
      ),
      (d) => d.yAxisID === OBSERVED_SPEND_ID,
      "desc",
    )
      .map(
        (
          dataset: ChartDataset<"line" | "bar", unknown[]> & {
            borderDash: number[];
            id?: string;
          },
        ) => {
          let value = dataset.data[dataIndex] ?? 0;
          let label = dataset.label;

          if (dataset.yAxisID === REVENUE_ID) {
            value = options.isRevenue
              ? MetricFormatters.currency(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  { precision: "exact" },
                )
              : MetricFormatters.decimal(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  { precision: "exact" },
                );
          }

          if (dataset.yAxisID === ROAS_ID) {
            value = options.isRevenue
              ? MetricFormatters.multiplier(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  { precision: "exact" },
                )
              : MetricFormatters.currency(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  { precision: "exact" },
                );
          }

          if (dataset.yAxisID === OBSERVED_SPEND_ID) {
            const observedSpendDataIndex =
              tooltip.dataPoints.find(
                (dataPoint) => dataPoint.dataset.yAxisID === OBSERVED_SPEND_ID,
              )?.dataIndex ?? 0;
            value = `${dataset.data[observedSpendDataIndex] ?? 0} weeks`;

            if (observedSpendIsSelectedAlone) {
              const labels = tooltip.chart.scales.histogramX.getLabels();
              if (labels && Number.isInteger(observedSpendDataIndex)) {
                label += ` (${labels[observedSpendDataIndex]})`;
              }
            } else {
              const chartData = getChartData(tooltip.chart);
              const spendValue = getSpendForTooltipLocation();
              if (chartData?.getBucketFromSpendValue && spendValue) {
                label += ` (${chartData.getBucketFromSpendValue(
                  Number(spendValue),
                )})`;
              }
            }
          }

          const legend = buildLegend(
            {
              borderColor: dataset.borderColor,
              backgroundColor: dataset.backgroundColor,
              borderDash: dataset.borderDash ?? [],
            } as any,
            dataset.type === "bar",
          );

          return `
            <div class="flex space-x-0.5 items-center font-normal">
              <div style="min-width: 32px; width: 32px;">${legend}</div>
              <div>${
                dataset.yAxisID === REVENUE_ID ? "Expected " : ""
              }${label}</div>
            </div>
            <div class="text-right font-mono font-normal">${value}</div>
          `;
        },
      )
      .join("");
  };

  return `
    <div class="grid grid-cols-2 text-xs gap-2">
      ${getSpendSection()}
      ${getTooltipDataPointsData()}
    </div>
  `;
};
